import { useState } from 'react';
import { Eye, Info, Pencil, Trash2, Users } from 'lucide-react';

import { CallDetailPage } from '@/pages/Call/CallDetailPage/CallDetailPage';

import { PagePagination } from '@/components/pagination';
import { ProtectedComponent } from '@/components/ProtectedComponent';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownAction } from '@/components/ui/dropdown-actions';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import {
  useBatchCallRecipientsQuery,
  useDeleteBatchCallRecipientMutation,
} from '@/query/scheduler.query';

import { DEFAULT_PAGE_SIZE, Permissions } from '@/constants';

import { RecipientUpdateSheet } from './RecipientUpdateSheet';

import useAlertStore from '@/store/alert.store';
import { useRecipientListStore } from '@/store/schedule.store';
import type { BatchRecipient } from '@/types/scheduler.type';

interface RecipientsTableProps {
  batchCallId: number;
}

function recipientInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0]?.toUpperCase() ?? '')
    .join('');
}

function StatusBadge({ status, failureReason }: { status: string; failureReason: string | null }) {
  const variants: Record<string, string> = {
    success: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    failed: 'bg-red-100 text-red-700',
    in_progress: 'bg-blue-100 text-blue-700',
  };
  const cls = variants[status.toLowerCase()] ?? 'bg-muted text-muted-foreground';
  const isFailed = status.toLowerCase() === 'failed';

  const badge = (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium capitalize ${cls}`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );

  if (!isFailed) return badge;

  return (
    <div className="flex items-center gap-1">
      {badge}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {isFailed && <Info className="h-3 w-3 shrink-0" />}
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-60">
            {failureReason ?? 'Call failed. No additional details available.'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export function RecipientsTable({ batchCallId }: RecipientsTableProps) {
  const { currentPage, pageSize, setCurrentPage, setPageSize } = useRecipientListStore();
  const [editingRecipient, setEditingRecipient] = useState<BatchRecipient | null>(null);
  const [selectedCallId, setSelectedCallId] = useState<number | null>(null);
  const openAlert = useAlertStore(s => s.openAlert);

  const { data, isLoading } = useBatchCallRecipientsQuery(batchCallId, {
    page: currentPage,
    page_size: pageSize,
  });

  const { mutate: deleteRecipient } = useDeleteBatchCallRecipientMutation(batchCallId);

  const handleDelete = (recipient: BatchRecipient) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openAlert({
      title: 'Delete Recipient',
      description: `Are you sure you want to delete "${recipient.name}"? This action cannot be undone.`,
      type: 'destructive',
      confirmText: 'Delete',
      onConfirm: () => deleteRecipient(recipient.id),
    });
  };

  const recipients = data?.data ?? [];
  const meta = data?.meta;

  return (
    <>
      <Card className="bg-muted rounded-2xl p-1 pt-0 gap-0 border-0 shadow-none flex-1">
        <CardHeader className="flex flex-col px-5 py-3">
          <CardTitle className="text-sm">Recipients</CardTitle>
        </CardHeader>
        <CardContent className="bg-card p-6 rounded-xl shadow-sm flex flex-col">
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader className="bg-muted">
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Recipient name</TableHead>
                  <TableHead>Phone number</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="h-32 text-center text-muted-foreground text-sm"
                    >
                      Loading recipients…
                    </TableCell>
                  </TableRow>
                ) : recipients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
                        <Users className="h-12 w-12 text-muted-foreground/40" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium">No recipients found</p>
                          <p className="text-xs">
                            Recipients will appear here once the batch call is created
                          </p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  recipients.map((recipient, index) => {
                    const rowNum = (currentPage - 1) * pageSize + index + 1;
                    return (
                      <TableRow
                        key={recipient.id}
                        className={recipient.callId ? 'cursor-pointer hover:bg-muted/50 group' : ''}
                        onClick={() => {
                          if (recipient.callId) setSelectedCallId(recipient.callId);
                        }}
                      >
                        <TableCell className="text-muted-foreground text-sm">{rowNum}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-7 w-7 text-xs">
                              <AvatarFallback>{recipientInitials(recipient.name)}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-medium">{recipient.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{recipient.phoneNumber}</TableCell>
                        <TableCell>
                          <StatusBadge
                            status={recipient.status}
                            failureReason={recipient.failureReason}
                          />
                        </TableCell>
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <DropdownAction>
                            {recipient.callId && (
                              <ProtectedComponent
                                requiredPermissions={[Permissions.CallsReadDetail]}
                              >
                                <DropdownMenuItem
                                  onClick={() => setSelectedCallId(recipient.callId!)}
                                >
                                  <Eye className="h-4 w-4" />
                                  <span>View</span>
                                </DropdownMenuItem>
                              </ProtectedComponent>
                            )}

                            <ProtectedComponent
                              requiredPermissions={[Permissions.BatchCallCreateUpdate]}
                            >
                              <DropdownMenuItem onClick={() => setEditingRecipient(recipient)}>
                                <Pencil className="" />
                                <span>Edit</span>
                              </DropdownMenuItem>
                            </ProtectedComponent>

                            <ProtectedComponent requiredPermissions={[Permissions.BatchCallDelete]}>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={handleDelete(recipient)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                                <span>Delete</span>
                              </DropdownMenuItem>
                            </ProtectedComponent>
                          </DropdownAction>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <PagePagination
            isLoading={isLoading}
            currentPage={meta?.page || 1}
            pageSize={meta?.page_size || DEFAULT_PAGE_SIZE}
            totalItems={meta?.total_items || 0}
            totalPages={meta?.total_pages || 1}
            setPageSize={size => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            setCurrentPage={setCurrentPage}
          />
        </CardContent>
      </Card>

      {editingRecipient && (
        <RecipientUpdateSheet
          batchCallId={batchCallId}
          recipient={editingRecipient}
          open={!!editingRecipient}
          onClose={() => setEditingRecipient(null)}
        />
      )}

      <ProtectedComponent requiredPermissions={[Permissions.CallsReadDetail]}>
        {selectedCallId && (
          <CallDetailPage callId={selectedCallId} toggle={() => setSelectedCallId(null)} />
        )}
      </ProtectedComponent>
    </>
  );
}
